// Single reconciled body-builder for the solution wizard (shell-unify). PURE: no
// dispatch, no toasts, no network — just serviceValue -> request body. Callers
// validate first, then persist the returned body.
//
//   mode "create" -> metaTags included; no part-filter
//   mode "edit"   -> metaTags excluded; PART_BODY_FIELDS filter applied iff a
//                    `partHeld` predicate is supplied (duplication = "edit" with
//                    partHeld omitted => full authorship, no filter)
//   Unified in BOTH modes: refs use obj.id || obj._id; logoPath sent only when a
//   cover is set. This replaces the two divergent inline builders (create fired
//   as a Device-step mount side-effect; edit via handleSave) with one function.
//
// PARITY: for every reachable input this produces a persisted-doc-equal body to
// the legacy create (Devices) / edit (Edit handleSave) code, per mode. See
// src/utils/__tests__/buildServicePayload.test.js (golden diff).
export function buildServicePayload(serviceValue, { mode, partHeld } = {}) {
  const isCreate = mode === "create";

  // metaData.tabs (identical both modes)
  const metaData = {
    tabs: {
      location: serviceValue.metaData.location,
      maintenance: serviceValue.metaData.maintenance,
      videoAnalytics: serviceValue.metaData.videoAnalytics,
      digitalTwin: { value: serviceValue.metaData.digitalTwin, svg: serviceValue.svg },
    },
  };
  if (
    serviceValue.layout == 1 &&
    (!metaData.tabs.digitalTwin || !metaData.tabs.digitalTwin.value)
  ) {
    metaData.tabs.digitalTwin = { value: true, svg: "test-svg" };
  }

  // assetMapping transform (identical both modes): fold valueInsights into
  // sensors, rename assetId -> assetType. Deep-cloned so the slice is untouched.
  const assetMapping = JSON.parse(JSON.stringify([...serviceValue.assetMapping]));
  for (let i = 0; i < assetMapping.length; i++) {
    const o = assetMapping[i];
    o.sensors = o.sensors.concat(o.valueInsights);
    o.assetType = o.assetId;
    delete o.assetId;
    delete o.valueInsights;
  }

  // trend: edit re-resolves defaultDatapoint against configuredSensors; create
  // sends it raw (at create the default is freshly built and valid).
  let trend = serviceValue.trend;
  if (!isCreate && serviceValue.trend) {
    trend = JSON.parse(JSON.stringify(serviceValue.trend));
    if (serviceValue.trend && serviceValue.trend.defaultDatapoint && serviceValue.trend.defaultDatapoint.id) {
      const found = serviceValue.configuredSensors.find(
        (s) =>
          (s && (s.id || s._id)) === serviceValue.trend.defaultDatapoint.id &&
          s.typeValue === "datapoint"
      );
      const r = found || serviceValue.configuredSensors.find((s) => s && s.typeValue === "datapoint");
      trend.defaultDatapoint = { id: r && (r.id || r._id), name: r && r.name };
    }
  }

  // groupMetaData: edit drops "Air Flow Rate" when layout != 1; create sends raw.
  let groupMetaData = serviceValue.groupMetaData;
  if (!isCreate && serviceValue.layout != 1) {
    groupMetaData = serviceValue.groupMetaData.filter((it) => it.key !== "Air Flow Rate");
  }

  const l1 = serviceValue.layout == 1;
  const body = {
    name: serviceValue.name,
    description: serviceValue.description,
    tags: serviceValue.tags,
    configuredSensors: serviceValue.configuredSensors.map((o) => o.id || o._id),
    configuredActuators: serviceValue.configuredActuators.map((o) => o.id || o._id),
    metaData,
    configuredAsset: serviceValue.asset[0],
    configuredAssets: serviceValue.asset,
    assetMapping,
    dataPointThresholds: serviceValue.dataPointThresholds,
    parentChildEnabled: serviceValue.parentChildEnabled,
    groupMetaData,
    layout: { map: serviceValue.map, dashboardView: serviceValue.layout },
    widgetDatapoints: serviceValue.widgetDatapoints,
    trend,
    defaultLocation: serviceValue.defaultLocation,
    energySavingEnable: l1 ? serviceValue.energySavingEnable : false,
    currency: l1 ? serviceValue.currency : "GBP",
    multiplier: l1 ? serviceValue.multiplier : isCreate ? 1 : "1", // legacy parity drift, preserved
    temperatureUnit: l1 ? serviceValue.temperatureUnit : "celsius",
  };

  if (isCreate) body.metaTags = serviceValue.meta; // create-only
  if (serviceValue.cover) body.logoPath = serviceValue.cover; // unified conditional

  if (serviceValue.vanish) {
    body.vanish = serviceValue.vanish;
    body.devicePrompt = serviceValue.devPrompt;
    body.duration = serviceValue.duration;
  } else {
    body.vanish = serviceValue.vanish;
  }

  // PART_BODY_FIELDS allow-list — EDIT only, only when a partHeld predicate is
  // supplied. Mirrors BE grantCheck.js PART_FIELDS (BE stays authoritative).
  if (mode === "edit" && partHeld) {
    const PART_BODY_FIELDS = {
      "Key Info": ["name", "description", "tags", "metaTags", "parentChildEnabled", "metaData", "currency", "multiplier", "temperatureUnit", "logoPath"],
      "Asset": ["configuredAsset", "configuredAssets", "esbAssetMapping"],
      "Controlling": ["configuredActuators"],
      "Configurations": ["vanish", "devicePrompt", "duration", "groupMetaData", "defaultLocation", "trend", "profile"],
      "Layout": ["layout", "widgetDatapoints", "energySavingEnable", "target", "cost"],
      "Threshold Management": ["dataPointThresholds"],
    };
    Object.entries(PART_BODY_FIELDS).forEach(([part, fields]) => {
      if (!partHeld(part)) fields.forEach((f) => delete body[f]);
    });
    // configuredSensors owned by Monitoring OR Value Insights.
    if (!partHeld("Monitoring") && !partHeld("Value Insights")) delete body.configuredSensors;
    // assetMapping co-owned by Asset OR Monitoring OR Value Insights OR Controlling.
    if (
      !partHeld("Asset") &&
      !partHeld("Monitoring") &&
      !partHeld("Value Insights") &&
      !partHeld("Controlling")
    )
      delete body.assetMapping;
  }

  return body;
}
